/* eslint-disable no-restricted-globals */
import { useEffect, useRef } from 'react';

type UseServiceWorkerOptions = {
  onUpdateReady?: () => void;
  onUpdateFound?: () => void;
};

export function useServiceWorker({
  onUpdateReady,
  onUpdateFound,
}: UseServiceWorkerOptions = {}) {
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let registration: ServiceWorkerRegistration;

    const register = async () => {
      registration = await navigator.serviceWorker.register('/sw.js');

      registration.addEventListener('updatefound', () => {
        onUpdateFound?.();
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (
            newWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            waitingWorkerRef.current = newWorker;
            onUpdateReady?.();
          }
        });
      });
    };

    register();

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SKIP_WAITING') {
        waitingWorkerRef.current?.postMessage({ type: 'SKIP_WAITING' });
      }
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [onUpdateFound, onUpdateReady]);

  const applyUpdate = () => {
    if (waitingWorkerRef.current) {
      waitingWorkerRef.current.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  return { applyUpdate };
}
